import React, { useState, useRef, useCallback } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Send, Loader2 } from 'lucide-react';
import { postsApi } from '../../services/postsApi';

// Comment input with @mention autocomplete. On submit calls onSubmit(text).
const CommentComposer = ({ language = 'en', onSubmit, testIdPrefix = 'comment' }) => {
  const es = language === 'es';
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const tokenRef = useRef(null); // {start, end}

  const runSearch = useCallback(async (q) => {
    try {
      const users = await postsApi.searchUsers(q);
      setMatches(users || []);
      setOpen((users || []).length > 0);
    } catch {
      setMatches([]); setOpen(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart ?? val.length;
    const upto = val.slice(0, caret);
    const m = upto.match(/@([a-z0-9_]{1,20})$/i);
    if (m) {
      tokenRef.current = { start: caret - m[0].length, end: caret };
      runSearch(m[1]);
    } else {
      setOpen(false);
      tokenRef.current = null;
    }
  };

  const pickMention = (u) => {
    const handle = u.username || '';
    if (!handle || !tokenRef.current) { setOpen(false); return; }
    const { start, end } = tokenRef.current;
    const next = `${text.slice(0, start)}@${handle} ${text.slice(end)}`;
    setText(next);
    setOpen(false);
    setMatches([]);
    tokenRef.current = null;
    inputRef.current?.focus();
  };

  const submit = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      await onSubmit(text.trim());
      setText('');
      setOpen(false);
    } catch {
      // keep the draft so the user can retry (or sign in first)
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="relative">
      {open && matches.length > 0 && (
        <div
          className="absolute bottom-full left-0 mb-1 w-full max-w-xs bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden"
          data-testid={`${testIdPrefix}-mention-dropdown`}
        >
          {matches.map((u) => (
            <button
              key={u.user_id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pickMention(u); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-accent transition-colors"
              data-testid={`mention-option-${u.user_id}`}
            >
              <Avatar className="w-6 h-6">
                {u.picture ? <AvatarImage src={u.picture} /> : null}
                <AvatarFallback className="text-[10px] bg-primary/15">
                  {(u.name || '?').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium truncate">{u.name}</span>
              {u.username && <span className="text-xs text-muted-foreground truncate">@{u.username}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <Input
          ref={inputRef}
          value={text}
          onChange={handleChange}
          onKeyDown={(e) => { if (e.key === 'Enter' && !open) submit(); }}
          placeholder={es ? 'Añade un comentario… usa @ para mencionar' : 'Add a comment… use @ to mention'}
          className="h-9"
          data-testid={`${testIdPrefix}-input`}
        />
        <Button size="icon" variant="ghost" onClick={submit} disabled={posting} data-testid={`${testIdPrefix}-submit`}>
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

export default CommentComposer;
